import { useEffect, useCallback, useRef } from 'react';
import { logError } from 'components/LogError';
import { ChatMessage } from '../types';

const DEFAULT_STORAGE_KEY = 'cultwines_chat_history';
const MAX_MESSAGES = 100;

interface PersistedChatData {
  messages: ChatMessage[];
  lastUpdated: number;
}

/**
 * Load persisted messages from localStorage
 */
const loadPersistedMessages = (key: string = DEFAULT_STORAGE_KEY): ChatMessage[] => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return [];

    const data: PersistedChatData = JSON.parse(stored);

    // Filter out invalid messages that would break the API:
    // - streaming messages (incomplete)
    // - tool result messages (orphaned without preceding tool_calls)
    // - assistant messages with toolCalls (incomplete without tool results)
    const validMessages = data.messages.filter((msg) => {
      if (msg.isStreaming) return false;
      if (msg.role === 'tool') return false;
      if (msg.role === 'assistant' && msg.toolCalls && msg.toolCalls.length > 0) return false;
      return true;
    });

    return validMessages;
  } catch (error) {
    logError(`Failed to load chat history: ${(error as Error).message}`);
    return [];
  }
};

/**
 * Save messages to localStorage
 */
const persistMessages = (messages: ChatMessage[], key: string = DEFAULT_STORAGE_KEY): void => {
  try {
    // Filter out:
    // - streaming messages (incomplete)
    // - tool result messages (transient)
    // - assistant messages with toolCalls (incomplete without tool results)
    const messagesToPersist = messages
      .filter((msg) => {
        if (msg.isStreaming) return false;
        if (msg.role === 'tool') return false;
        if (msg.role === 'assistant' && msg.toolCalls && msg.toolCalls.length > 0) return false;
        return true;
      })
      .slice(-MAX_MESSAGES);

    const data: PersistedChatData = {
      messages: messagesToPersist,
      lastUpdated: Date.now(),
    };

    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    // localStorage might be full or disabled
    logError(`Failed to persist chat history: ${(error as Error).message}`);
  }
};

/**
 * Clear persisted messages
 */
const clearPersistedMessages = (key: string = DEFAULT_STORAGE_KEY): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    logError(`Failed to clear chat history: ${(error as Error).message}`);
  }
};

interface UseChatPersistenceOptions {
  enabled?: boolean;
  debounceMs?: number;
  storageKey?: string;
}

interface UseChatPersistenceReturn {
  loadMessages: () => ChatMessage[];
  saveMessages: (messages: ChatMessage[]) => void;
  clearMessages: () => void;
}

/**
 * Hook for managing chat message persistence
 */
export const useChatPersistence = (options: UseChatPersistenceOptions = {}): UseChatPersistenceReturn => {
  const { enabled = true, debounceMs = 1000, storageKey } = options;
  const key = storageKey || DEFAULT_STORAGE_KEY;
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, []);

  const loadMessages = useCallback((): ChatMessage[] => {
    if (!enabled) return [];
    return loadPersistedMessages(key);
  }, [enabled, key]);

  const saveMessages = useCallback(
    (messages: ChatMessage[]): void => {
      if (!enabled) return;

      // Debounce saves to avoid excessive writes
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      saveTimeoutRef.current = setTimeout(() => {
        persistMessages(messages, key);
      }, debounceMs);
    },
    [enabled, debounceMs, key],
  );

  const clearMessages = useCallback((): void => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    clearPersistedMessages(key);
  }, [key]);

  return {
    loadMessages,
    saveMessages,
    clearMessages,
  };
};
